import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  DeleteOutline as DeleteOutlineIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
} from "@mui/icons-material";

import {
  formatDateTime,
  formatTime,
  getDifferenceColor,
  getMovementAmountColor,
  getMovementChannelLabel,
  getMovementReasonLabel,
  getMovementSignedAmount,
  getMovementStatusLabel,
  getVoidedByLabel,
  movementChannelOptions,
  movementReasonOptions,
  toMoney,
  toSignedMoney,
} from "../cashSessionShared";

export function MetricCard({ label, value, helper, tooltip, icon: Icon, accent = "primary.main", sx = {} }) {
  const card = (
    <Card
      variant="outlined"
      sx={{
        minWidth: 170,
        borderRadius: 1.5,
        borderColor: (theme) => alpha(theme.palette.divider, 0.65),
        borderLeft: 4,
        borderLeftColor: accent,
        boxShadow: "0 8px 20px rgba(15,23,42,0.035)",
        ...sx,
      }}
    >
      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.35 }}>
          {Icon ? (
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: 1.2,
                display: "grid",
                placeItems: "center",
                color: accent,
                bgcolor: (theme) => alpha(theme.palette.text.primary, 0.035),
              }}
            >
              <Icon sx={{ fontSize: 15 }} />
            </Box>
          ) : null}
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0 }}>
            {label}
          </Typography>
        </Stack>
        <Typography variant="h6" sx={{ fontWeight: 500, lineHeight: 1.15 }}>
          {value}
        </Typography>
        {helper ? (
          <Typography variant="body2" color="text.secondary">
            {helper}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
  if (!tooltip) return card;
  return (
    <Tooltip title={tooltip} arrow>
      <Box>{card}</Box>
    </Tooltip>
  );
}

export function ChannelSummaryCard({ title, expected, counted, difference, backgroundColor, isSessionOpen }) {
  return (
    <Tooltip title={`${title}: esperado, contado por cajero y diferencia del metodo de pago.`} arrow>
    <Card
      variant="outlined"
      sx={{
        minWidth: 190,
        bgcolor: backgroundColor,
        borderRadius: 1.25,
        border: "0.5px solid rgba(16, 23, 60, 0.12)",
      }}
    >
      <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 0.4 }}>
          {title}
        </Typography>
        <Typography variant="body2">Esperado: {toMoney(expected)}</Typography>
        <Typography variant="body2">Contado: {counted == null ? "-" : toMoney(counted)}</Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: getDifferenceColor(difference, isSessionOpen),
          }}
        >
          Diferencia: {difference == null ? "-" : toSignedMoney(difference)}
        </Typography>
      </CardContent>
    </Card>
    </Tooltip>
  );
}

export function MovementInlinePanel({
  title = "Registrar egreso",
  form,
  onChange,
  onSubmit,
  onCancel,
  submitting = false,
}) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 1.5 }}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={1.25}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
          <FormControl size="small" fullWidth>
            <InputLabel>Razon</InputLabel>
            <Select
              label="Razon"
              value={form.reason_type}
              onChange={(event) => onChange("reason_type", event.target.value)}
            >
              {movementReasonOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Canal</InputLabel>
            <Select
              label="Canal"
              value={form.payment_channel}
              onChange={(event) => onChange("payment_channel", event.target.value)}
            >
              {movementChannelOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Monto"
            size="small"
            type="number"
            value={form.amount}
            onChange={(event) => onChange("amount", event.target.value)}
            inputProps={{ min: 0, step: "0.01" }}
            fullWidth
          />
          <TextField
            label="Descripcion"
            size="small"
            multiline
            minRows={2}
            value={form.description}
            onChange={(event) => onChange("description", event.target.value)}
            fullWidth
          />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={onCancel} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={onSubmit} disabled={submitting}>
              Guardar egreso
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function CloseSessionInlinePanel({
  title,
  submitLabel,
  summary,
  form,
  onChange,
  onSubmit,
  onCancel,
  submitting = false,
}) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 1.5 }}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={1.25}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Esperado efectivo: {toMoney(summary?.expected_cash)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Esperado tarjeta: {toMoney(summary?.expected_card)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Esperado transferencia: {toMoney(summary?.expected_transfer)}
          </Typography>
          <TextField
            label="Contado efectivo"
            size="small"
            type="number"
            value={form.counted_cash}
            onChange={(event) => onChange("counted_cash", event.target.value)}
            inputProps={{ min: 0, step: "0.01" }}
            fullWidth
          />
          <TextField
            label="Contado tarjeta"
            size="small"
            type="number"
            value={form.counted_card}
            onChange={(event) => onChange("counted_card", event.target.value)}
            inputProps={{ min: 0, step: "0.01" }}
            fullWidth
          />
          <TextField
            label="Contado transferencia"
            size="small"
            type="number"
            value={form.counted_transfer}
            onChange={(event) => onChange("counted_transfer", event.target.value)}
            inputProps={{ min: 0, step: "0.01" }}
            fullWidth
          />
          <TextField
            label="Nota de cierre"
            size="small"
            multiline
            minRows={2}
            value={form.closing_note}
            onChange={(event) => onChange("closing_note", event.target.value)}
            fullWidth
          />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={onCancel} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="contained" color="warning" onClick={onSubmit} disabled={submitting}>
              {submitLabel}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function VoidMovementInlinePanel({
  movement,
  reason,
  onReasonChange,
  onSubmit,
  onCancel,
  submitting = false,
}) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 1.5, borderColor: "error.light" }}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={1.25}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            Anular movimiento
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Movimiento #{movement?.id || "-"} · {getMovementReasonLabel(movement?.reason_type)} ·{" "}
            {toSignedMoney(getMovementSignedAmount(movement))}
          </Typography>
          <TextField
            label="Motivo de anulacion"
            size="small"
            multiline
            minRows={2}
            value={reason}
            onChange={(event) => onReasonChange(event.target.value)}
            fullWidth
          />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={onCancel} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="contained" color="error" onClick={onSubmit} disabled={submitting}>
              Confirmar anulacion
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function CashSessionMovementsTable({
  movements,
  variant = "cashier",
  canView = false,
  isViewEnabled,
  onViewClick,
  getMovementTypeLabel,
  canVoid = false,
  sessionOpen = false,
  onVoidClick,
  emptyMessage,
}) {
  const isAdmin = variant === "admin";
  const rows = Array.isArray(movements) ? movements : [];
  const resolveTypeLabel = (movement) => {
    if (typeof getMovementTypeLabel === "function") {
      return getMovementTypeLabel(movement);
    }
    if (movement?.reason_type === "SALE") return "Bien/producto";
    return movement?.movement_type === "OUT" ? "Egreso" : "Ingreso";
  };

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{isAdmin ? "Fecha" : "Hora"}</TableCell>
            <TableCell>Razon</TableCell>
            <TableCell>Tipo</TableCell>
            <TableCell>Canal</TableCell>
            <TableCell align="right">Monto</TableCell>
            <TableCell>Descripcion</TableCell>
            <TableCell>Estado</TableCell>
            {canView ? <TableCell align="center">Ver</TableCell> : null}
            {canVoid ? <TableCell align="right">Anular</TableCell> : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7 + (canView ? 1 : 0) + (canVoid ? 1 : 0)}>
                <Typography variant="body2" color="text.secondary">
                  {emptyMessage}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((movement) => (
              <TableRow
                key={movement.id}
                sx={{
                  opacity: movement.status === "VOID" ? 0.45 : 1,
                }}
              >
                <TableCell>{isAdmin ? formatDateTime(movement.occurred_at || movement.created_at) : formatTime(movement.occurred_at || movement.created_at)}</TableCell>
                <TableCell>{getMovementReasonLabel(movement.reason_type)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={resolveTypeLabel(movement)}
                    color={resolveTypeLabel(movement) === "Servicio grooming" ? "info" : "default"}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{getMovementChannelLabel(movement.payment_channel)}</TableCell>
                <TableCell align="right">
                  <Typography sx={{ color: getMovementAmountColor(movement) }}>
                    {toSignedMoney(getMovementSignedAmount(movement))}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{movement.description || "-"}</Typography>
                  {movement.status === "VOID" && movement.void_reason ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Motivo: {movement.void_reason}
                    </Typography>
                  ) : null}
                  {isAdmin && movement.status === "VOID" ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Anulado por: {getVoidedByLabel(movement)}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={getMovementStatusLabel(movement.status)}
                    color={movement.status === "VOID" ? "default" : "success"}
                    variant={movement.status === "VOID" ? "outlined" : "filled"}
                  />
                </TableCell>
                {canView ? (
                  <TableCell align="center">
                    <Tooltip title="Ver detalle">
                      <span>
                        <IconButton
                          size="small"
                          disabled={typeof isViewEnabled === "function" ? !isViewEnabled(movement) : false}
                          onClick={() => onViewClick?.(movement)}
                        >
                          <VisibilityOutlinedIcon fontSize="inherit" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                ) : null}
                {canVoid ? (
                  <TableCell align="right">
                    <Tooltip title="Anular movimiento">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={!sessionOpen || movement.status === "VOID"}
                          onClick={() => onVoidClick?.(movement)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                ) : null}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Box>
  );
}
